import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    suicideDetails: [],
    currentSuicideDetail: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Suicide Details
export const fetchSuicideDetails = createAsyncThunk(
    'suicideDetails/fetchSuicideDetails',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/suicide-details');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch suicide details'
            );
        }
    }
);

// Fetch a single Suicide Detail
export const fetchSuicideDetail = createAsyncThunk(
    'suicideDetails/fetchSuicideDetail',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/suicide-details/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch suicide detail'
            );
        }
    }
);

// Create a new Suicide Detail
export const createSuicideDetail = createAsyncThunk(
    'suicideDetails/createSuicideDetail',
    async (suicideData, { rejectWithValue }) => {
        try {
            const response = await api.post('/suicide-details', suicideData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create suicide detail'
            );
        }
    }
);

// Update an existing Suicide Detail (by ID)
export const updateSuicideDetail = createAsyncThunk(
    'suicideDetails/updateSuicideDetail',
    async ({ case_id, suicideData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/suicide-details/case/${case_id}`, suicideData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update suicide detail'
            );
        }
    }
);

// Delete a Suicide Detail
export const deleteSuicideDetail = createAsyncThunk(
    'suicideDetails/deleteSuicideDetail',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/suicide-details/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete suicide detail'
            );
        }
    }
);

// ==================== Slice ====================
const suicideDetailsSlice = createSlice({
    name: 'suicideDetails',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentSuicideDetail: (state, action) => {
            state.currentSuicideDetail = action.payload;
        },
        clearCurrentSuicideDetail: (state) => {
            state.currentSuicideDetail = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchSuicideDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSuicideDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.suicideDetails = action.payload;
            })
            .addCase(fetchSuicideDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchSuicideDetail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSuicideDetail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentSuicideDetail = action.payload;
            })
            .addCase(fetchSuicideDetail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createSuicideDetail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createSuicideDetail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.suicideDetails.unshift(action.payload);
            })
            .addCase(createSuicideDetail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateSuicideDetail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSuicideDetail.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.suicideDetails.findIndex((d) => d.id === updated.id);
                if (index !== -1) {
                    state.suicideDetails[index] = updated;
                }

                if (state.currentSuicideDetail?.id === updated.id) {
                    state.currentSuicideDetail = updated;
                }
            })
            .addCase(updateSuicideDetail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteSuicideDetail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteSuicideDetail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.suicideDetails = state.suicideDetails.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentSuicideDetail?.id === action.payload) {
                    state.currentSuicideDetail = null;
                }
            })
            .addCase(deleteSuicideDetail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setCurrentSuicideDetail,
    clearCurrentSuicideDetail,
} = suicideDetailsSlice.actions;

export default suicideDetailsSlice.reducer;
